/**
 * hooks/useAIOrchestration.ts
 *
 * Heartbeat that drives continuous event-triggered AI re-evaluation.
 * Wires: changeDetection → alertEngine → analyzeSymbol → thesisEngine
 */

import { useEffect, useMemo, useRef, useCallback } from 'react';
import { useStore } from '../store';
import { now } from '../utils/clock';
import { buildSymbolContextForAI } from '../services/buildSymbolContext';
import { getSymbolContext } from '../selectors/symbolContext';
import { detectChanges, shouldTriggerRefresh, ChangeEvent } from '../ai/changeDetection';
import { analyzeSymbol, getOrchestratorStatus, subscribeOrchestratorStatus } from '../ai/aiOrchestrator';
import { getThesis, checkStaleness, subscribeThesis } from '../ai/thesisEngine';
import { alertsFromChangeEvents, alertFromThesisChange, alertFromAIStatus, alertFromFeedStatus } from '../features/alerts/alertEngine';
import { logEvent } from '../features/status/systemEventLog';
import { DataFeedStatus } from './useMarketData';


export type AutoAIMode = 'off' | 'major_only' | 'all';

export interface AIOrchestrationConfig {
  autoMode:         AutoAIMode;
  cooldownMs:       number;
  scanIntervalMs:   number;
  activeSymbolOnly: boolean;
}

const DEFAULT_CONFIG: AIOrchestrationConfig = {
  autoMode:         'major_only',
  cooldownMs:       30_000,
  scanIntervalMs:   5_000,
  activeSymbolOnly: true,
};

// Track last AI refresh per symbol
const lastRefreshMap = new Map<string, number>();
// Track last triggering event description per symbol (shown in UI)
const lastTriggerMap = new Map<string, string>();

export function getLastTrigger(symbol: string): string {
  return lastTriggerMap.get(symbol) || '';
}

export function useAIOrchestration(
  config: Partial<AIOrchestrationConfig> = {},
  feedStatus?: DataFeedStatus
) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const cfg = useMemo(() => ({ ...DEFAULT_CONFIG, ...config }), [config]);
  const { watchlist, activeSymbol, symbolData, settings, weights } = useStore();
  const scanTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Feed status alerts
  const prevFeedRef = useRef<DataFeedStatus | null>(null);
  useEffect(() => {
    if (feedStatus === undefined) return;
    const connected = feedStatus === 'live' || feedStatus === 'live-tick' || feedStatus === 'polling';
    const prevConnected = prevFeedRef.current !== null
      ? (prevFeedRef.current === 'live' || prevFeedRef.current === 'live-tick' || prevFeedRef.current === 'polling')
      : null;
    if (prevConnected !== null) alertFromFeedStatus(connected);
    prevFeedRef.current = feedStatus;
  }, [feedStatus]);

  // AI status alerts
  useEffect(() => {
    const unsub = subscribeOrchestratorStatus(() => {
      alertFromAIStatus(getOrchestratorStatus());
    });
    return unsub;
  }, []);

  // Thesis change alerts
  useEffect(() => {
    const unsub = subscribeThesis(() => {
      // Check every symbol for thesis changes
      watchlist.forEach(({ symbol }) => {
        const t = getThesis(symbol);
        if (t.current && t.updateCount > 0) {
          const ctx  = getSymbolContext(symbol, symbolData[symbol], settings, weights);
          const gate = ctx ? {
            setupActionable:  ctx.setupActionable,
            topSetupAlignment: ctx.setups[0]?.alignment,
            topSetupMaturity:  ctx.setups[0]?.maturity,
          } : undefined;
          alertFromThesisChange(symbol, t, gate);
        }
      });
    });
    return unsub;
  }, [watchlist, symbolData, settings, weights]);

  const buildContext = useCallback((symbol: string) => {
    return buildSymbolContextForAI(symbol, symbolData[symbol], settings, weights);
  }, [symbolData, settings, weights]);

  const evaluateSymbol = useCallback(async (symbol: string) => {
    const data = symbolData[symbol];
    if (!data?.candles?.length) return;

    const ctx = getSymbolContext(symbol, data, settings, weights);
    if (!ctx) return;
    const { smc, regime, traps } = ctx;
    const thesis = getThesis(symbol);

    // Build gate from live context for this symbol
    const gate = {
      setupActionable:   ctx.setupActionable,
      topSetupAlignment: ctx.setups[0]?.alignment,
      topSetupMaturity:  ctx.setups[0]?.maturity,
    };

    // Detect changes and emit alerts for all meaningful events
    const events: ChangeEvent[] = detectChanges(symbol, ctx.candles, regime, smc, traps, ctx.zones, thesis);
    if (events.length) {
      alertsFromChangeEvents(symbol, events, gate);
      // Log major events to system trace
      events.filter(e => e.significance === 'major').forEach(e =>
        logEvent('detection', e.description, symbol, e.type)
      );
    }

    if (cfg.autoMode === 'off') return;

    // Decide if AI should refresh
    const lastRefresh = lastRefreshMap.get(symbol) || null;
    const decision = shouldTriggerRefresh(
      events,
      cfg.autoMode,
      lastRefresh,
      cfg.cooldownMs
    );

    if (!decision.shouldRefresh) return;

    // Record trigger reason for UI display
    lastTriggerMap.set(symbol, decision.reason);
    lastRefreshMap.set(symbol, now());
    logEvent('ai', `AI refresh triggered — ${decision.reason}`, symbol, decision.highestSig || undefined);

    const aiCtx = buildContext(symbol);
    if (!aiCtx) return;
    await analyzeSymbol(aiCtx, false);
  }, [symbolData, settings, weights, cfg, buildContext]);

  // Main scan loop
  useEffect(() => {
    const scan = async () => {
      checkStaleness();
      const symbolsToCheck = cfg.activeSymbolOnly
        ? (activeSymbol ? [activeSymbol] : [])
        : watchlist.map(w => w.symbol);

      for (const symbol of symbolsToCheck) {
        if (getOrchestratorStatus().inFlight.has(symbol)) continue;
        await evaluateSymbol(symbol);
      }
    };

    if (scanTimerRef.current) clearInterval(scanTimerRef.current);
    if (cfg.autoMode !== 'off') {
      scanTimerRef.current = setInterval(scan, cfg.scanIntervalMs);
    }
    return () => { if (scanTimerRef.current) clearInterval(scanTimerRef.current); };
  }, [cfg.autoMode, cfg.activeSymbolOnly, cfg.scanIntervalMs, cfg.cooldownMs,
      activeSymbol, watchlist, evaluateSymbol]);

  // Manual trigger
  const triggerNow = useCallback(async (symbol: string) => {
    const ctx = buildContext(symbol);
    if (!ctx) return;
    lastRefreshMap.set(symbol, now());
    lastTriggerMap.set(symbol, 'Manual run');
    logEvent('ai', 'AI refresh triggered — Manual run', symbol);
    return analyzeSymbol(ctx, true);
  }, [buildContext]);

  return { triggerNow };
}
